import type { RampConfig, RampShape } from '@gorampe/calc-engine';
import { optimizeTreadSections } from '@gorampe/calc-engine';
import type {
  SceneLayout,
  PlatformPlacement,
  TreadPlacement,
  RailPlacement,
  LandingPlacement,
  PattePlacement,
  CornerPostPlacement,
  TransitionPlacement,
} from '../types';
import { SCALE_IN_TO_UNIT } from '../types';
import type { HardwareGroup } from '../hardware/instances';
import {
  DEFAULT_HEAD_PLATFORM_DEPTH_IN,
  DEFAULT_HEAD_PLATFORM_WIDTH_IN,
  RAIL_CLEARANCE_IN,
  EDGE_OVERLAP_IN,
} from './constants';
import { selectPatteSku, selectTransitionSku, selectCornerPostSku } from '../bom/skuMapping';

type U180Shape = Extract<RampShape, { kind: 'U180' }>;

export function layoutU180(config: RampConfig, shape: U180Shape): SceneLayout {
  const platforms: PlatformPlacement[] = [];
  const treads: TreadPlacement[] = [];
  const rails: RailPlacement[] = [];
  const landings: LandingPlacement[] = [];
  const pattes: PattePlacement[] = [];
  const cornerPosts: CornerPostPlacement[] = [];
  const PATTE_INTERVAL_IN = 60; // patte every 5 ft

  // PDF officiel: head platform 60×60 (default) or 60×96.
  // U180 turn landing keeps its existing shape.landingDepthIn (already paramaterized).
  const headDepthIn = config.headPlatformDepthIn ?? DEFAULT_HEAD_PLATFORM_DEPTH_IN;
  const headWidthIn = DEFAULT_HEAD_PLATFORM_WIDTH_IN;

  const totalRun = shape.arm1RunIn + shape.arm2RunIn;
  const arm1Height = config.heightIn * (shape.arm1RunIn / totalRun);
  const arm2Height = config.heightIn - arm1Height;
  const turnYIn = arm2Height;
  const armPitch = Math.atan2(arm1Height, shape.arm1RunIn);
  const arm2Pitch = Math.atan2(arm2Height, shape.arm2RunIn);

  const parallelOffsetIn = shape.landingDepthIn + RAIL_CLEARANCE_IN;

  // Head platform at top of arm1
  // PDF officiel: 60×60 (5×5) or 60×96 (5×8), independent of arm width.
  platforms.push({
    id: 'head',
    kind: 'head',
    position: {
      x: 0,
      y: config.heightIn * SCALE_IN_TO_UNIT,
      z: (-headDepthIn / 2 + EDGE_OVERLAP_IN) * SCALE_IN_TO_UNIT,
    },
    widthIn: headWidthIn,
    depthIn: headDepthIn,
  });

  // Turn (180°) landing at end of arm1 / start of arm2
  const landingCenterZ = shape.arm1RunIn + shape.landingDepthIn / 2;
  const landingWidthIn = config.widthIn + parallelOffsetIn;
  platforms.push({
    id: 'turn',
    kind: 'turn-landing',
    position: {
      x: (-parallelOffsetIn / 2) * SCALE_IN_TO_UNIT,
      y: turnYIn * SCALE_IN_TO_UNIT,
      z: landingCenterZ * SCALE_IN_TO_UNIT,
    },
    widthIn: landingWidthIn,
    depthIn: shape.landingDepthIn,
  });

  // Arm 1 treads — rotationY = 0, running along +Z
  let c1 = 0;
  for (const sec of optimizeTreadSections(shape.arm1RunIn)) {
    for (let i = 0; i < sec.qty; i++) {
      const mid = c1 + sec.lengthIn / 2;
      const y = config.heightIn - (mid / shape.arm1RunIn) * arm1Height;
      treads.push({
        id: `tread-a1-${sec.lengthIn}-${i}-${c1}`,
        position: { x: 0, y: y * SCALE_IN_TO_UNIT, z: mid * SCALE_IN_TO_UNIT },
        rotationY: 0,
        pitchAngle: armPitch,
        lengthIn: sec.lengthIn,
        widthIn: config.widthIn,
      });
      c1 += sec.lengthIn;
    }
  }

  // Arm 2 treads — rotationY = PI (reversed direction), offset -X
  // Pitch POSITIF pour conserver continuité du sol entre treads.
  // (Tentative pitch -arm2Pitch précédemment a causé discontinuité du sol
  // car chaque tread tilt opposé crée des sauts Y aux junctions de 2×sin(p)×len.)
  // L'inversion visuelle "angle inverse" est obtenue uniquement par rotationY=π.
  const arm2OriginZ = shape.arm1RunIn + shape.landingDepthIn;
  let c2 = 0;
  for (const sec of optimizeTreadSections(shape.arm2RunIn)) {
    for (let i = 0; i < sec.qty; i++) {
      const mid = c2 + sec.lengthIn / 2;
      const y = turnYIn - (mid / shape.arm2RunIn) * arm2Height;
      treads.push({
        id: `tread-a2-${sec.lengthIn}-${i}-${c2}`,
        position: {
          x: -parallelOffsetIn * SCALE_IN_TO_UNIT,
          y: y * SCALE_IN_TO_UNIT,
          z: (arm2OriginZ - mid) * SCALE_IN_TO_UNIT,
        },
        rotationY: Math.PI,
        pitchAngle: arm2Pitch, // POSITIF — continuité du sol garantie
        lengthIn: sec.lengthIn,
        widthIn: config.widthIn,
      });
      c2 += sec.lengthIn;
    }
  }

  // Rails: 2 per arm (left + right) = 4 total — pitched + at mid-slope height
  if (config.railType !== 'aucun') {
    const rh = config.usage === 'com' ? 42 : 36;
    const xOff = (config.widthIn / 2) * SCALE_IN_TO_UNIT;
    const arm1MidY = config.heightIn - arm1Height / 2;
    const arm2MidY = turnYIn - arm2Height / 2;

    // Head platform side rails ONLY (left + right). NO back rail — building/doorway side
    const headTopY = config.heightIn * SCALE_IN_TO_UNIT;
    const headMidZ = (-headDepthIn / 2 + EDGE_OVERLAP_IN) * SCALE_IN_TO_UNIT;
    rails.push({
      id: 'rail-head-left',
      position: { x: -xOff, y: headTopY, z: headMidZ },
      rotationY: 0,
      pitchAngle: 0,
      lengthIn: headDepthIn,
      heightIn: rh,
      type: config.railType,
      side: 'left',
    });
    rails.push({
      id: 'rail-head-right',
      position: { x: xOff, y: headTopY, z: headMidZ },
      rotationY: 0,
      pitchAngle: 0,
      lengthIn: headDepthIn,
      heightIn: rh,
      type: config.railType,
      side: 'right',
    });

    for (const side of ['left', 'right'] as const) {
      rails.push({
        id: `rail-a1-${side}`,
        position: {
          x: side === 'left' ? -xOff : xOff,
          y: arm1MidY * SCALE_IN_TO_UNIT,
          z: (shape.arm1RunIn / 2) * SCALE_IN_TO_UNIT,
        },
        rotationY: 0,
        pitchAngle: armPitch,
        lengthIn: shape.arm1RunIn,
        heightIn: rh,
        type: config.railType,
        side,
      });
      // arm2 rail covers only from ground (arm2BottomZ) to south edge of landing (arm1RunIn).
      // The landing-west rail covers the segment inside the landing (Z=[arm1RunIn, arm2OriginZ]).
      // Without this trim, arm2 rail and landing-west would double-overlap on the landing segment.
      const arm2RailLength = shape.arm2RunIn - shape.landingDepthIn;
      const arm2GroundZ = arm2OriginZ - shape.arm2RunIn; // = arm2BottomZ
      const arm2RailCenterZ = arm2GroundZ + arm2RailLength / 2;
      // Rails arm 2 — pitch POSITIF pour descendre de palier (haut) à sol (bas)
      // dans le sens de la fondation. (Les treads ont -arm2Pitch pour le look
      // visual miroir, mais les rails doivent suivre la direction de descente
      // des centres de treads, pas leur tilt individuel.)
      rails.push({
        id: `rail-a2-${side}`,
        position: {
          x: -parallelOffsetIn * SCALE_IN_TO_UNIT + (side === 'left' ? -xOff : xOff),
          y: arm2MidY * SCALE_IN_TO_UNIT,
          z: arm2RailCenterZ * SCALE_IN_TO_UNIT,
        },
        rotationY: Math.PI,
        pitchAngle: arm2Pitch, // POSITIF — suit le sens de descente de la fondation
        lengthIn: arm2RailLength,
        heightIn: rh,
        type: config.railType,
        side,
      });
    }

    // U-turn landing perimeter rails — landing spans Z=[arm1RunIn, arm1RunIn+landingDepth]
    // and X=[-(parallelOffset+widthIn/2), +widthIn/2]. Open sides:
    //  - Far side (+Z, the U-bend) extends along X
    //  - East long side (+X edge) extends along Z, between arm1 and arm2 rails
    //  - West long side (-X edge) extends along Z, same logic
    const landingFarZ = shape.arm1RunIn + shape.landingDepthIn;
    const landingMidZ = shape.arm1RunIn + shape.landingDepthIn / 2;
    const eastX = config.widthIn / 2;
    const westX = -parallelOffsetIn - config.widthIn / 2;
    // Far side rail — extends along X (rotationY=π/2)
    rails.push({
      id: 'rail-landing-far',
      position: {
        x: (-parallelOffsetIn / 2) * SCALE_IN_TO_UNIT,
        y: turnYIn * SCALE_IN_TO_UNIT,
        z: landingFarZ * SCALE_IN_TO_UNIT,
      },
      rotationY: Math.PI / 2,
      pitchAngle: 0,
      lengthIn: parallelOffsetIn + config.widthIn,
      heightIn: rh,
      type: config.railType,
      side: 'right',
    });
    // East long side rail — extends along Z (rotationY=0)
    rails.push({
      id: 'rail-landing-east',
      position: {
        x: eastX * SCALE_IN_TO_UNIT,
        y: turnYIn * SCALE_IN_TO_UNIT,
        z: landingMidZ * SCALE_IN_TO_UNIT,
      },
      rotationY: 0,
      pitchAngle: 0,
      lengthIn: shape.landingDepthIn,
      heightIn: rh,
      type: config.railType,
      side: 'right',
    });
    // West long side rail — extends along Z (rotationY=0)
    rails.push({
      id: 'rail-landing-west',
      position: {
        x: westX * SCALE_IN_TO_UNIT,
        y: turnYIn * SCALE_IN_TO_UNIT,
        z: landingMidZ * SCALE_IN_TO_UNIT,
      },
      rotationY: 0,
      pitchAngle: 0,
      lengthIn: shape.landingDepthIn,
      heightIn: rh,
      type: config.railType,
      side: 'left',
    });

    // Landing perimeter CLOSURE rails — close the south/north edges where arms don't cover.
    // Arm 1 covers x=±xOffIn at z=arm1RunIn (south edge). Arm 2 covers x=-parallelOffset±xOffIn at z=landingFarZ (north edge).
    // Both closures must leave the passage open for the arm that transitions at that edge.
    //
    // South edge (z=arm1RunIn): arm2 descends through here (X=[-153,-117]).
    //   South-left closure spans from arm2-right edge (-parallelOffset+xOffIn) to arm1-left edge (-xOffIn).
    //   This leaves X=[westX, -parallelOffset+xOffIn] = arm2 passage open — symmetric to north-right.
    //
    // North edge (z=landingFarZ): arm1 would ascend through X=[-18,+18] but arm1 stops at z=arm1RunIn.
    //   North-right closure spans from arm2-right edge (-parallelOffset+xOffIn) to arm1-right edge (+xOffIn).
    //   This leaves X=[westX, -parallelOffset+xOffIn] = arm2 passage open at north edge.
    const xOffIn = config.widthIn / 2;
    // arm2 right edge X (same in both south and north closures)
    const arm2RightX = -parallelOffsetIn + xOffIn;
    // South-left closure: from arm2-right edge to arm1-left edge (leaves arm2 passage open at south)
    const southLeftStartX = arm2RightX; // -parallelOffset+xOffIn
    const southLeftEndX = -xOffIn;
    const southLeftLength = southLeftEndX - southLeftStartX; // positive
    const southLeftCenterX = (southLeftStartX + southLeftEndX) / 2;
    rails.push({
      id: 'rail-landing-south-left',
      position: {
        x: southLeftCenterX * SCALE_IN_TO_UNIT,
        y: turnYIn * SCALE_IN_TO_UNIT,
        z: shape.arm1RunIn * SCALE_IN_TO_UNIT,
      },
      rotationY: Math.PI / 2,
      pitchAngle: 0,
      lengthIn: southLeftLength,
      heightIn: rh,
      type: config.railType,
      side: 'left',
    });
    // North-right closure: from -parallelOffset+xOffIn (arm2 east edge) to eastX
    const northRightLength = eastX - (-parallelOffsetIn + xOffIn);
    const northRightCenterX = (-parallelOffsetIn + xOffIn + eastX) / 2;
    rails.push({
      id: 'rail-landing-north-right',
      position: {
        x: northRightCenterX * SCALE_IN_TO_UNIT,
        y: turnYIn * SCALE_IN_TO_UNIT,
        z: landingFarZ * SCALE_IN_TO_UNIT,
      },
      rotationY: Math.PI / 2,
      pitchAngle: 0,
      lengthIn: northRightLength,
      heightIn: rh,
      type: config.railType,
      side: 'right',
    });
  }

  // Pattes — arm 1: x=0, running along +Z
  const arm1PatteCount = Math.max(1, Math.floor(shape.arm1RunIn / PATTE_INTERVAL_IN));
  for (let i = 1; i <= arm1PatteCount; i++) {
    const fraction = i / (arm1PatteCount + 1);
    const zIn = shape.arm1RunIn * fraction;
    // arm1 top Y = config.heightIn, arm1 bottom Y = config.heightIn - arm1Height = turnYIn
    const patteYIn = config.heightIn - fraction * arm1Height;
    if (patteYIn < 1) continue;
    pattes.push({
      id: `patte-a1-${i}`,
      position: {
        x: 0,
        y: patteYIn * SCALE_IN_TO_UNIT,
        z: zIn * SCALE_IN_TO_UNIT,
      },
      rotationY: 0,
      widthIn: config.widthIn,
      heightIn: patteYIn,
      partNumber: selectPatteSku(patteYIn),
    });
  }

  // Pattes — arm 2: x=-parallelOffsetIn, running along -Z from arm2OriginZ
  const arm2PatteCount = Math.max(1, Math.floor(shape.arm2RunIn / PATTE_INTERVAL_IN));
  for (let i = 1; i <= arm2PatteCount; i++) {
    const fraction = i / (arm2PatteCount + 1);
    // arm2 runs from arm2OriginZ (high Y=turnYIn) to arm2OriginZ - arm2RunIn (Y=0)
    const zIn = arm2OriginZ - shape.arm2RunIn * fraction;
    const patteYIn = turnYIn - fraction * arm2Height;
    if (patteYIn < 1) continue;
    pattes.push({
      id: `patte-a2-${i}`,
      position: {
        x: -parallelOffsetIn * SCALE_IN_TO_UNIT,
        y: patteYIn * SCALE_IN_TO_UNIT,
        z: zIn * SCALE_IN_TO_UNIT,
      },
      rotationY: 0,
      widthIn: config.widthIn,
      heightIn: patteYIn,
      partNumber: selectPatteSku(patteYIn),
    });
  }

  // Transition plate at arm2 ground exit
  const arm2BottomZ = arm2OriginZ - shape.arm2RunIn;
  const transitions: TransitionPlacement[] = [
    {
      id: 'trans-arm2-base',
      position: {
        x: -parallelOffsetIn * SCALE_IN_TO_UNIT,
        y: 0,
        z: (arm2BottomZ - 5) * SCALE_IN_TO_UNIT,
      },
      rotationY: Math.PI, // arm2 walks -Z direction
      widthIn: config.widthIn,
      partNumber: selectTransitionSku(config.widthIn, config.floor),
    },
  ];

  // Corner posts — 2 at head platform south + 4 at landing perimeter
  if (config.railType !== 'aucun') {
    const rh = config.usage === 'com' ? 42 : 36;
    const cpSku = selectCornerPostSku(rh);
    const landingFarZ = shape.arm1RunIn + shape.landingDepthIn;
    const eastX = config.widthIn / 2;
    const westX = -parallelOffsetIn - config.widthIn / 2;
    // Head platform south corners (where arm1 rails start)
    cornerPosts.push({
      id: 'cp-head-sw',
      position: {
        x: -(config.widthIn / 2) * SCALE_IN_TO_UNIT,
        y: config.heightIn * SCALE_IN_TO_UNIT,
        z: EDGE_OVERLAP_IN * SCALE_IN_TO_UNIT,
      },
      heightIn: rh,
      partNumber: cpSku,
    });
    cornerPosts.push({
      id: 'cp-head-se',
      position: {
        x: (config.widthIn / 2) * SCALE_IN_TO_UNIT,
        y: config.heightIn * SCALE_IN_TO_UNIT,
        z: EDGE_OVERLAP_IN * SCALE_IN_TO_UNIT,
      },
      heightIn: rh,
      partNumber: cpSku,
    });
    // Landing 4 corners
    for (const z of [shape.arm1RunIn, landingFarZ]) {
      for (const x of [eastX, westX]) {
        cornerPosts.push({
          id: `cp-landing-${z}-${x}`,
          position: {
            x: x * SCALE_IN_TO_UNIT,
            y: turnYIn * SCALE_IN_TO_UNIT,
            z: z * SCALE_IN_TO_UNIT,
          },
          heightIn: rh,
          partNumber: cpSku,
        });
      }
    }

    // Corner posts at bottom of arm 2 (ground level, where arm 2 rails end)
    // Arm 2 runs along -Z from arm2OriginZ, offset by -parallelOffsetIn in X.
    // Its rails are at x=(-parallelOffsetIn ± widthIn/2), z=arm2BottomZ
    const arm2BottomZ = arm2OriginZ - shape.arm2RunIn;
    const xOff = config.widthIn / 2;
    cornerPosts.push({
      id: 'cp-arm2-sw',
      position: {
        x: (-parallelOffsetIn - xOff) * SCALE_IN_TO_UNIT,
        y: 0,
        z: arm2BottomZ * SCALE_IN_TO_UNIT,
      },
      heightIn: rh,
      partNumber: cpSku,
    });
    cornerPosts.push({
      id: 'cp-arm2-se',
      position: {
        x: (-parallelOffsetIn + xOff) * SCALE_IN_TO_UNIT,
        y: 0,
        z: arm2BottomZ * SCALE_IN_TO_UNIT,
      },
      heightIn: rh,
      partNumber: cpSku,
    });
  }

  const hardwareGroups: HardwareGroup[] = [];

  return {
    platforms,
    treads,
    rails,
    landings,
    pattes,
    cornerPosts,
    transitions,
    hardwareGroups,
    floor: config.floor,
  };
}
